import React, { useState, useMemo } from 'react';
import { ChevronDown } from 'lucide-react';
import KITCHEN_ITEMS from '../data/kitchenItems';
import PANTRY_PRESETS from '../data/pantryPresets';

const CATEGORY_GROUPS = [
  { label: 'All', categories: null },
  { label: 'Produce', categories: ['Fruits', 'Vegetables', 'Herbs'] },
  { label: 'Proteins', categories: ['Meat', 'Seafood', 'Dairy', 'Legumes'] },
  { label: 'Pantry', categories: ['Grains', 'Baking', 'Oils', 'Condiments', 'Canned', 'Spices'] },
  { label: 'Extras', categories: ['Nuts & Seeds', 'Frozen', 'Beverages', 'Spreads', 'International', 'Dried Fruits'] },
];

const KITCHEN_ITEMS_BY_NAME = Object.fromEntries(KITCHEN_ITEMS.map((i) => [i.name, i]));

const shelfDaysToExpiry = (days) => {
  if (!days) return '';
  return new Date(Date.now() + days * 86400000).toISOString().split('T')[0];
};

const buildInventoryItems = (names) =>
  names.map((name) => {
    const db = KITCHEN_ITEMS_BY_NAME[name];
    return {
      name,
      quantity: 1,
      servings: 1,
      price: 0,
      unit: db?.unit || 'grams',
      expiryDate: db ? shelfDaysToExpiry(db.shelfDays) : '',
    };
  });

const QuickStartPanel = ({ onAdd, onDismiss, existingNames }) => {
  const [mode, setMode] = useState('presets');
  const [expandedPresetId, setExpandedPresetId] = useState(null);
  const [deselected, setDeselected] = useState(new Set());    // items unchecked within expanded preset
  const [pickedItems, setPickedItems] = useState(new Set());  // items chosen in Pick Your Own
  const [activeGroup, setActiveGroup] = useState('All');

  const existingSet = useMemo(
    () => new Set((existingNames || []).map((n) => n.toLowerCase())),
    [existingNames]
  );

  // ── Preset tab ────────────────────────────────────────────────────────────

  const togglePreset = (presetId) => {
    if (expandedPresetId === presetId) {
      setExpandedPresetId(null);
      setDeselected(new Set());
    } else {
      setExpandedPresetId(presetId);
      setDeselected(new Set());
    }
  };

  const toggleDeselect = (name) => {
    setDeselected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const handleAddAll = (preset) => {
    const newItems = preset.items.filter((n) => !existingSet.has(n.toLowerCase()));
    onAdd(buildInventoryItems(newItems));
    setExpandedPresetId(null);
    setDeselected(new Set());
  };

  const handleAddSelected = (preset) => {
    const selected = preset.items.filter(
      (n) => !deselected.has(n) && !existingSet.has(n.toLowerCase())
    );
    if (selected.length === 0) return;
    onAdd(buildInventoryItems(selected));
    setExpandedPresetId(null);
    setDeselected(new Set());
  };

  // ── Pick Your Own tab ─────────────────────────────────────────────────────

  const filteredItems = useMemo(() => {
    const group = CATEGORY_GROUPS.find((g) => g.label === activeGroup);
    if (!group || !group.categories) return KITCHEN_ITEMS;
    return KITCHEN_ITEMS.filter((item) => group.categories.includes(item.category));
  }, [activeGroup]);

  const togglePick = (name) => {
    setPickedItems((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const handleAddPicked = () => {
    if (pickedItems.size === 0) return;
    onAdd(buildInventoryItems([...pickedItems]));
    setPickedItems(new Set());
  };

  // ── Derived counts ────────────────────────────────────────────────────────

  const expandedPreset = PANTRY_PRESETS.find((p) => p.id === expandedPresetId);
  const newItemsInPreset = expandedPreset
    ? expandedPreset.items.filter((n) => !existingSet.has(n.toLowerCase()))
    : [];
  const selectedCount = newItemsInPreset.filter((n) => !deselected.has(n)).length;

  return (
    <div className="quickstart-panel">
      <div className="quickstart-header">
        <div>
          <h2 className="quickstart-title">Quick-start your pantry</h2>
          <p className="quickstart-subtitle">Add your staples in seconds — no typing required.</p>
        </div>
        {onDismiss && (
          <button onClick={onDismiss} className="btn btn-ghost btn-sm">Skip</button>
        )}
      </div>

      <div className="quickstart-mode-tabs" role="tablist">
        <button
          role="tab"
          aria-selected={mode === 'presets'}
          className={`quickstart-mode-tab${mode === 'presets' ? ' active' : ''}`}
          onClick={() => setMode('presets')}
        >
          Starter Kits
        </button>
        <button
          role="tab"
          aria-selected={mode === 'pick'}
          className={`quickstart-mode-tab${mode === 'pick' ? ' active' : ''}`}
          onClick={() => setMode('pick')}
        >
          Pick Your Own
        </button>
      </div>

      {/* ── Preset Kit Cards ── */}
      {mode === 'presets' && (
        <div className="preset-list">
          {PANTRY_PRESETS.map((preset) => {
            const isExpanded = expandedPresetId === preset.id;
            const newCount = preset.items.filter((n) => !existingSet.has(n.toLowerCase())).length;

            return (
              <div key={preset.id} className={`preset-card-v2${isExpanded ? ' expanded' : ''}`}>
                {/* Card header — always visible */}
                <button
                  className="preset-card-header"
                  onClick={() => togglePreset(preset.id)}
                  aria-expanded={isExpanded}
                >
                  <span className="preset-emoji" aria-hidden="true">{preset.emoji}</span>
                  <div className="preset-card-info">
                    <span className="preset-name">{preset.name}</span>
                    <span className="preset-desc">{preset.description}</span>
                  </div>
                  <span className="preset-count-badge">{newCount} new</span>
                  <ChevronDown
                    size={16}
                    className={`preset-chevron${isExpanded ? ' rotated' : ''}`}
                    aria-hidden="true"
                  />
                </button>

                {/* Expanded body — item selection */}
                {isExpanded && (
                  <div className="preset-card-body">
                    <div className="quickpick-grid" role="group" aria-label={`Items in ${preset.name}`}>
                      {preset.items.map((name) => {
                        const already = existingSet.has(name.toLowerCase());
                        const unchecked = deselected.has(name);
                        const isNew = !already;
                        return (
                          <button
                            key={name}
                            onClick={() => !already && toggleDeselect(name)}
                            disabled={already}
                            className={`quickpick-chip${isNew && !unchecked ? ' selected' : ''}${already ? ' already' : ''}`}
                            aria-pressed={isNew && !unchecked}
                            title={already ? 'Already in inventory' : undefined}
                          >
                            {name}
                            {already && <span className="quickpick-chip-mark" aria-hidden="true">·</span>}
                            {isNew && !unchecked && <span className="quickpick-chip-mark" aria-hidden="true">✓</span>}
                          </button>
                        );
                      })}
                    </div>

                    <div className="preset-card-actions">
                      <button
                        className="btn btn-primary"
                        onClick={() => handleAddAll(preset)}
                        disabled={newCount === 0}
                      >
                        Add all {newCount} items
                      </button>
                      {deselected.size > 0 && selectedCount > 0 && (
                        <button
                          className="btn btn-secondary"
                          onClick={() => handleAddSelected(preset)}
                        >
                          Add {selectedCount} selected
                        </button>
                      )}
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => { setExpandedPresetId(null); setDeselected(new Set()); }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Pick Your Own ── */}
      {mode === 'pick' && (
        <>
          <div className="quickpick-tabs" role="tablist">
            {CATEGORY_GROUPS.map((g) => (
              <button
                key={g.label}
                role="tab"
                aria-selected={activeGroup === g.label}
                className={`quickpick-tab${activeGroup === g.label ? ' active' : ''}`}
                onClick={() => setActiveGroup(g.label)}
              >
                {g.label}
              </button>
            ))}
          </div>

          <div
            className="quickpick-grid"
            role="listbox"
            aria-multiselectable="true"
            aria-label="Choose pantry items"
          >
            {filteredItems.map((item) => {
              const selected = pickedItems.has(item.name);
              const already = existingSet.has(item.name.toLowerCase());
              return (
                <button
                  key={item.name}
                  role="option"
                  aria-selected={selected}
                  disabled={already}
                  onClick={() => !already && togglePick(item.name)}
                  className={`quickpick-chip${selected ? ' selected' : ''}${already ? ' already' : ''}`}
                  title={already ? 'Already in inventory' : item.category}
                >
                  {item.name}
                  {selected && <span className="quickpick-chip-mark" aria-hidden="true">✓</span>}
                  {already && <span className="quickpick-chip-mark" aria-hidden="true">·</span>}
                </button>
              );
            })}
          </div>

          {pickedItems.size > 0 && (
            <div className="quickstart-cta">
              <button onClick={handleAddPicked} className="btn btn-primary" style={{ flex: 1 }}>
                Add {pickedItems.size} item{pickedItems.size !== 1 ? 's' : ''} to Pantry
              </button>
              <button onClick={() => setPickedItems(new Set())} className="btn btn-ghost">
                Clear
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default QuickStartPanel;
